import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_SYSTEM_STATS } from '../../../graphql/queries/Auth/QueryAuth';
import {
  Pane,
  Heading,
  Spinner,
  Text,
  Card,
  Strong,
  Alert,
  Paragraph,
  Table
} from 'evergreen-ui';

const SystemStatsBox = () => {
  const { loading, error, data } = useQuery(GET_SYSTEM_STATS, {
    fetchPolicy: 'network-only'
  });

  // Debug the response
  useEffect(() => {
    if (data) {
      console.log('SystemStatsBox - data:', data);
      try {
        const parsedOnce = JSON.parse(data.systemStats);
        console.log('SystemStatsBox - parsed once:', parsedOnce);

        if (typeof parsedOnce === 'string') {
          try {
            const parsedTwice = JSON.parse(parsedOnce);
            console.log('SystemStatsBox - parsed twice:', parsedTwice);
          } catch (e) {
            console.error('Error parsing inner JSON:', e);
          }
        }
      } catch (e) {
        console.error('Error parsing JSON:', e);
      }
    }
  }, [data]);

  if (loading) {
    return (
      <Pane
        elevation={1}
        background="white"
        padding={16}
        marginBottom={16}
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={300}
      >
        <Spinner />
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane
        elevation={1}
        background="white"
        padding={16}
        marginBottom={16}
      >
        <Alert intent="danger" title="Error loading system statistics">
          {error.message}
        </Alert>
      </Pane>
    );
  }

  // Parse the JSON string from the GraphQL response - handle double encoding
  const stats = data?.systemStats ? (() => {
    try {
      // The response is a JSON string containing another JSON string
      const parsedOnce = JSON.parse(data.systemStats);

      // Check if the result is still a string (double encoded)
      if (typeof parsedOnce === 'string') {
        try {
          return JSON.parse(parsedOnce);
        } catch (e) {
          console.error('Error parsing inner JSON:', e);
          return parsedOnce;
        }
      }
      return parsedOnce;
    } catch (e) {
      console.error('Error parsing JSON:', e);
      return {};
    }
  })() : {};

  // Helper function to get stats value with fallback for different field naming conventions
  const getStat = (key, fallbackKey, defaultValue = 0) => {
    if (!stats) return defaultValue;
    return stats[key] !== undefined ? stats[key] :
           (stats[fallbackKey] !== undefined ? stats[fallbackKey] : defaultValue);
  };

  const StatCard = ({ title, value }) => (
    <Card
      elevation={0}
      backgroundColor="#f9f9fb"
      padding={16}
      marginRight={16}
      marginBottom={16}
      width={200}
      height={100}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      <Heading size={700} marginBottom={8}>{value}</Heading>
      <Text size={300}>{title}</Text>
    </Card>
  );

  return (
    <Pane
      elevation={1}
      background="white"
      padding={16}
      marginBottom={16}
    >
      <Heading size={600} marginBottom={16}>System Statistics</Heading>

      <Pane display="flex" flexWrap="wrap" marginBottom={24}>
        <StatCard title="Publishers" value={getStat('total_publishers', 'publisher_count', 0)} />
        <StatCard title="Products" value={getStat('total_products', 'product_count', 0)} />
        <StatCard title="Customers" value={getStat('total_customers', 'customer_count', 0)} />
        <StatCard title="Orders" value={getStat('total_orders', 'order_count', 0)} />
        <StatCard title="Creators" value={getStat('total_creators', 'creator_count', 0)} />
        <StatCard title="Users" value={getStat('total_users', 'user_count', 0)} />
        <StatCard title="Superusers" value={getStat('total_superusers', 'superuser_count', 0)} />
        <StatCard title="Active Publishers" value={getStat('total_active_publishers', 'active_publisher_count', 0)} />
      </Pane>

      {/* Display publisher stats side by side */}
      <Pane display="flex" flexWrap="wrap" marginBottom={24}>
        {/* Top Publishers by Product Count */}
        <Pane width="50%" paddingRight={8}>
          {stats.publishers_by_product_count && stats.publishers_by_product_count.length > 0 && (
            <Pane>
              <Heading size={500} marginBottom={8}>Top Publishers by Product Count</Heading>
              <Table>
                <Table.Head>
                  <Table.TextHeaderCell>Publisher</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Products</Table.TextHeaderCell>
                </Table.Head>
                <Table.Body>
                  {stats.publishers_by_product_count.map(publisher => (
                    <Table.Row key={publisher.id} height={24}>
                      <Table.TextCell paddingY={6}>{publisher.name}</Table.TextCell>
                      <Table.TextCell paddingY={6} isNumber>{publisher.product_count}</Table.TextCell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Pane>
          )}
        </Pane>

        {/* Top Publishers by Customer Count */}
        <Pane width="50%" paddingLeft={8}>
          {stats.publishers_by_customer_count && stats.publishers_by_customer_count.length > 0 && (
            <Pane>
              <Heading size={500} marginBottom={8}>Top Publishers by Customer Count</Heading>
              <Table>
                <Table.Head>
                  <Table.TextHeaderCell>Publisher</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Customers</Table.TextHeaderCell>
                </Table.Head>
                <Table.Body>
                  {stats.publishers_by_customer_count.map(publisher => (
                    <Table.Row key={publisher.id} height={24}>
                      <Table.TextCell paddingY={6}>{publisher.name}</Table.TextCell>
                      <Table.TextCell paddingY={6} isNumber>{publisher.customer_count}</Table.TextCell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Pane>
          )}
        </Pane>
      </Pane>

      {/* Display publisher stats side by side - Order Count and Creator Count */}
      <Pane display="flex" flexWrap="wrap" marginBottom={24}>
        {/* Top Publishers by Order Count */}
        <Pane width="50%" paddingRight={8}>
          {stats.publishers_by_order_count && stats.publishers_by_order_count.length > 0 && (
            <Pane>
              <Heading size={500} marginBottom={8}>Top Publishers by Order Count</Heading>
              <Table>
                <Table.Head>
                  <Table.TextHeaderCell>Publisher</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Orders</Table.TextHeaderCell>
                </Table.Head>
                <Table.Body>
                  {stats.publishers_by_order_count.map(publisher => (
                    <Table.Row key={publisher.id} height={24}>
                      <Table.TextCell paddingY={6}>{publisher.name}</Table.TextCell>
                      <Table.TextCell paddingY={6} isNumber>{publisher.order_count}</Table.TextCell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Pane>
          )}
        </Pane>

        {/* Top Publishers by Creator Count */}
        <Pane width="50%" paddingLeft={8}>
          {stats.publishers_by_creator_count && stats.publishers_by_creator_count.length > 0 && (
            <Pane>
              <Heading size={500} marginBottom={8}>Top Publishers by Creator Count</Heading>
              <Table>
                <Table.Head>
                  <Table.TextHeaderCell>Publisher</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Creators</Table.TextHeaderCell>
                </Table.Head>
                <Table.Body>
                  {stats.publishers_by_creator_count.map(publisher => (
                    <Table.Row key={publisher.id} height={24}>
                      <Table.TextCell paddingY={6}>{publisher.name}</Table.TextCell>
                      <Table.TextCell paddingY={6} isNumber>{publisher.creator_count}</Table.TextCell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Pane>
          )}
        </Pane>
      </Pane>
    </Pane>
  );
};

export default SystemStatsBox;
